import React, { useEffect, useMemo, useState } from 'react';
import { Button, Group, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { copyToClipboard } from '@/services/Utility';
import { EmbedIframeMonitorPreview } from './EmbedIframeMonitorPreview';
import {
  createIframeProps,
  createIframeString,
  EMBED_PREVIEW_REFERENCE_MONITOR_H,
  EMBED_PREVIEW_REFERENCE_MONITOR_W,
  IFRAME_EMBED_PRESETS,
  parseIframeDimensionForPreview,
} from './productEmbedUtils';
import classes from './ProductEmbedPanel.module.css';

export interface ProductEmbedPanelProps {
  productId: string;
  accentColor?: string;
  compact?: boolean;
}

export function ProductEmbedPanel({
  productId,
  accentColor = 'var(--mantine-color-gray-7)',
  compact = false,
}: ProductEmbedPanelProps) {
  const { t } = useTranslation();
  const [iframeWidth, setIframeWidth] = useState('');
  const [iframeHeight, setIframeHeight] = useState('400px');
  const [embedIncludeAnnotations, setEmbedIncludeAnnotations] = useState(true);
  const [embedIncludeAr, setEmbedIncludeAr] = useState(true);
  const [embedIncludePlayback, setEmbedIncludePlayback] = useState(true);
  const [embedIncludeAnimations, setEmbedIncludeAnimations] = useState(true);
  const [embedIncludeReset, setEmbedIncludeReset] = useState(true);
  const [embedIncludeHelp, setEmbedIncludeHelp] = useState(true);
  const [embedIncludeAppearance, setEmbedIncludeAppearance] = useState(true);

  const embedCopyOptions = useMemo(
    () => ({
      annotations: embedIncludeAnnotations,
      ar: embedIncludeAr,
      playback: embedIncludePlayback,
      animations: embedIncludeAnimations,
      reset: embedIncludeReset,
      help: embedIncludeHelp,
      appearance: embedIncludeAppearance,
    }),
    [
      embedIncludeAnnotations,
      embedIncludeAr,
      embedIncludePlayback,
      embedIncludeAnimations,
      embedIncludeReset,
      embedIncludeHelp,
      embedIncludeAppearance,
    ]
  );

  const [codeIframe, setCodeIframe] = useState<React.IframeHTMLAttributes<HTMLIFrameElement>>(
    () => createIframeProps(productId, iframeWidth, iframeHeight, embedCopyOptions)
  );

  useEffect(() => {
    setCodeIframe(createIframeProps(productId, iframeWidth, iframeHeight, embedCopyOptions));
  }, [productId, iframeWidth, iframeHeight, embedCopyOptions]);

  const handleCopyCode = async () => {
    const iframeString = createIframeString(codeIframe);
    if (!iframeString?.trim()) {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.embed.copyEmpty'),
        color: 'red',
      });
      return;
    }
    try {
      await copyToClipboard(iframeString);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('dashboard.notifications.iframeCopied'),
      });
    } catch {
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: t('dashboard.notifications.copyFailed'),
        color: 'red',
      });
    }
  };

  const inputSize = compact ? 'xs' : 'sm';

  return (
    <div className={classes.section}>
      <div>
        <Text className={classes.sectionTitle}>{t('dashboard.embed.previewTitle')}</Text>
        <Text className={classes.hint} mt={4}>
          {t('dashboard.embed.previewHint', {
            monitorW: EMBED_PREVIEW_REFERENCE_MONITOR_W,
            monitorH: EMBED_PREVIEW_REFERENCE_MONITOR_H,
          })}
        </Text>
      </div>

      <div className={classes.previewWrap}>
        <EmbedIframeMonitorPreview
          iframeProps={codeIframe}
          maxMonitorWidth={compact ? 460 : 440}
          maxScreenHeight={compact ? 200 : 300}
          compact={compact}
          approxSizeLabel={t('dashboard.embed.approxSize', {
            w: parseIframeDimensionForPreview(
              typeof codeIframe.width === 'number' ? `${codeIframe.width}px` : codeIframe.width,
              560,
              'w'
            ),
            h: parseIframeDimensionForPreview(
              typeof codeIframe.height === 'number' ? `${codeIframe.height}px` : codeIframe.height,
              360,
              'h'
            ),
          })}
        />
      </div>

      <div>
        <Text className={classes.sectionTitle}>{t('dashboard.embed.adjustHint')}</Text>
        <div className={classes.controlsRow}>
          <Select
            size={inputSize}
            style={{ flex: '1 1 200px', minWidth: 160 }}
            placeholder={t('dashboard.embed.preset')}
            data={IFRAME_EMBED_PRESETS.map((preset) => ({
              value: preset.value,
              label: preset.label,
            }))}
            onChange={(value: string | null) => {
              if (!value) return;
              const preset = IFRAME_EMBED_PRESETS.find((p) => p.value === value);
              if (preset) {
                setIframeWidth(preset.width);
                setIframeHeight(preset.height);
              }
            }}
          />
          <TextInput
            size={inputSize}
            placeholder="W"
            aria-label={t('dashboard.embed.widthPlaceholder')}
            value={iframeWidth}
            onChange={(e) => setIframeWidth(e.currentTarget.value)}
            style={{ width: 88 }}
          />
          <TextInput
            size={inputSize}
            placeholder="H"
            aria-label={t('dashboard.embed.heightPlaceholder')}
            value={iframeHeight}
            onChange={(e) => setIframeHeight(e.currentTarget.value)}
            style={{ width: 88 }}
          />
        </div>
      </div>

      <div>
        <Text className={classes.sectionTitle}>{t('dashboard.embed.viewerOptionsTitle')}</Text>
        <div className={classes.optionsGrid}>
          <Switch
            size="xs"
            label={t('dashboard.embed.optAnnotations')}
            checked={embedIncludeAnnotations}
            onChange={(e) => setEmbedIncludeAnnotations(e.currentTarget.checked)}
            color={accentColor}
          />
          <Switch
            size="xs"
            label={t('dashboard.embed.optAr')}
            checked={embedIncludeAr}
            onChange={(e) => setEmbedIncludeAr(e.currentTarget.checked)}
            color={accentColor}
          />
          <Switch
            size="xs"
            label={t('dashboard.embed.optPlayback')}
            checked={embedIncludePlayback}
            onChange={(e) => setEmbedIncludePlayback(e.currentTarget.checked)}
            color={accentColor}
          />
          <Switch
            size="xs"
            label={t('dashboard.embed.optAnimations')}
            checked={embedIncludeAnimations}
            onChange={(e) => setEmbedIncludeAnimations(e.currentTarget.checked)}
            color={accentColor}
          />
          <Switch
            size="xs"
            label={t('dashboard.embed.optReset')}
            checked={embedIncludeReset}
            onChange={(e) => setEmbedIncludeReset(e.currentTarget.checked)}
            color={accentColor}
          />
          <Switch
            size="xs"
            label={t('dashboard.embed.optHelp')}
            checked={embedIncludeHelp}
            onChange={(e) => setEmbedIncludeHelp(e.currentTarget.checked)}
            color={accentColor}
          />
          <Switch
            size="xs"
            label={t('dashboard.embed.optAppearance')}
            checked={embedIncludeAppearance}
            onChange={(e) => setEmbedIncludeAppearance(e.currentTarget.checked)}
            color={accentColor}
          />
        </div>
      </div>

      <div>
        <Text className={classes.sectionTitle}>{t('dashboard.embed.codeLabel')}</Text>
        <pre className={classes.codeBlock}>{createIframeString(codeIframe)}</pre>
      </div>

      <Group justify="flex-end">
        <Button color={accentColor} size="sm" onClick={() => void handleCopyCode()}>
          {t('dashboard.actions.copy')}
        </Button>
      </Group>
    </div>
  );
}
